// import '../../../../css/test-work-orders.css';
import { EditableAddressField } from '@/components/features/WorkOrders/components/EditableAddressField';
import { EditableField } from '@/components/features/WorkOrders/components/EditableField';
import { PhotoGallery } from '@/components/features/WorkOrders/components/PhotoGallery';
import { SetEstimatedDateSection } from '@/components/features/WorkOrders/components/SetEstimatedDateSection';
import { StatusBadge } from '@/components/features/WorkOrders/components/StatusBadge';
import { TypeBadge } from '@/components/features/WorkOrders/components/TypeBadge';
import { WorkOrderSidebar } from '@/components/features/WorkOrders/components/WorkOrderSidebar';
import { CancelWorkOrderDialog } from '@/components/features/WorkOrders/modals/CancelWorkOrderDialog';
import { CompleteWorkOrderDialog } from '@/components/features/WorkOrders/modals/CompleteWorkOrderDialog';
import { EditContactDialog } from '@/components/features/WorkOrders/modals/EditContactDialog';
import { EditWorkOrderDialog } from '@/components/features/WorkOrders/modals/EditWorkOrderDialog';
import { PhotoUploadDialog } from '@/components/features/WorkOrders/modals/PhotoUploadDialog';
import { AddressMapCard } from '@/components/global/address/AddressMapCard';
import UniversalComments from '@/components/global/comments/UniversalComments';
import { PriorityBadge } from '@/components/global/PriorityBadge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/shadcn/avatar';
import { Button } from '@/components/ui/shadcn/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { Separator } from '@/components/ui/shadcn/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { InlinePriorityCell } from '@/components/ui/table-cells/InlinePriorityCell';
import { InlineStatusCell } from '@/components/ui/table-cells/InlineStatusCell';
import { InlineTypeCell } from '@/components/ui/table-cells/InlineTypeCell';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { getCsrfToken } from '@/lib/csrf';
import { csrfFetch } from '@/lib/csrfFetch';
import { cn, formatDate, formatDateTime, useCan } from '@/lib/utils';
import { type Company, type SharedData, type User, type WorkOrder } from '@/types';
import { router, usePage } from '@inertiajs/react';
import { CheckCircle2, Edit, Image as ImageIcon, Mail, MoreVertical, Phone, Play, Upload, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react'; // Fragment
import { toast } from 'sonner';

interface WorkOrderShowProps {
    workOrder: WorkOrder;
    companies: Company[];
    /** Everyone who can be @-mentioned on this work order — broader than who may be assigned. */
    users: User[];
    /** Service Managers, Supervisors and Workers: the only roles a job can be assigned to. */
    assignableWorkers?: User[];
    statuses: Array<{ value: string; label: string; color: string; isTerminal?: boolean }>;
    priorities: Array<{ value: string; label: string; color: string; icon?: string }>;
    types: Array<{ value: string; label: string; color: string; icon?: string }>;
}

export default function WorkOrderShow({
    workOrder: initialWorkOrder,
    companies,
    users,
    assignableWorkers = [],
    statuses,
    priorities,
    types,
}: WorkOrderShowProps) {
    const { props } = usePage<SharedData>();
    const currentUser = props.auth.user;
    const { t } = useTranslations();
    const { can } = useCan();

    const canUpdateStatus = can('work-orders.update-status');
    const canCompleteWorkOrder = can('work-orders.complete-work-order');
    const canCancelWorkOrder = can('work-orders.can-cancel-work-order');
    const canUpdatePriority = can('work-orders.update-priority');
    const canUpdateType = can('work-orders.update-type');
    const canUpdateDetails = can('work-orders.update-details');
    const canAssignWorkersPermission = can('work-orders.assign');
    const canDeleteWorkOrder = can('work-orders.delete');
    // Note: Photo uploads are always allowed regardless of permissions (as per requirements)

    const [workOrder, setWorkOrder] = useState(initialWorkOrder);
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isContactEditDialogOpen, setIsContactEditDialogOpen] = useState(false);
    const [isWorkOrderEditDialogOpen, setIsWorkOrderEditDialogOpen] = useState(false);
    const [isPhotoUploadOpen, setIsPhotoUploadOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const isMobile = useIsMobile();

    // Sync workOrder state with prop when it changes (e.g., after reload)
    useEffect(() => {
        setWorkOrder(initialWorkOrder);
    }, [initialWorkOrder]);

    // Construct display address from structured fields if service_address is not available
    const displayAddress = useMemo(() => {
        return (
            workOrder.service_address ||
            (workOrder.service_address_street
                ? [workOrder.service_address_street, workOrder.service_address_house_number].filter(Boolean).join(' ')
                : '')
        );
    }, [workOrder.service_address, workOrder.service_address_street, workOrder.service_address_house_number]);

    const tabs = [
        { value: 'details', label: t('work_orders.show.tabs.details', 'Details') },
        { value: 'photos', label: t('work_orders.show.tabs.photos', 'Photos') },
        { value: 'comments', label: t('work_orders.show.tabs.comments', 'Comments') },
    ];

    const maxVisibleTabs = isMobile ? 3 : 5;
    const visibleTabs = tabs.slice(0, maxVisibleTabs);
    const overflowTabs = tabs.slice(maxVisibleTabs);
    const hasOverflow = overflowTabs.length > 0;

    const normalizeSettingValue = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '_');

    const translatedStatuses = useMemo(
        () =>
            statuses.map((status) => ({
                ...status,
                label: t(`settings.work_order_statuses.${normalizeSettingValue(status.value)}`, status.label),
            })),
        [statuses, t],
    );

    /**
     * Work order workflow (role-based + loop).
     *
     * Service Worker:
     * - registered -> in_progress (start work)
     * - in_progress -> completed (complete)
     * - in_progress -> parts/proposal needed (request proposal/parts)
     *
     * Service Manager/Supervisor/Admin:
     * - parts/proposal needed -> proposal_sent
     * - proposal_sent -> in_progress (proposal accepted, resume work)
     *
     * The stepper shows the canonical path with an explicit loop back to "in_progress".
     * We use activities to decide which "in_progress" node is currently active.
     */
    const workflow = useMemo(() => {
        const statusByValue = new Map((translatedStatuses ?? []).map((s) => [s.value, s]));

        const resolveStatusValue = (candidates: string[]): string | null => {
            for (const candidate of candidates) {
                if (statusByValue.has(candidate)) {
                    return candidate;
                }
            }
            return null;
        };

        const values = {
            registered: resolveStatusValue(['registered', 'pending']),
            inProgress: resolveStatusValue(['in_progress']),
            proposalNeeded: resolveStatusValue([
                // Common historic naming used in activity logs
                'parts_needed',
                // Proposal naming variants
                'proposal_needed',
                'proposal_required',
                'proposal_requested',
                // Sometimes modeled as "preparing proposal"
                'preparing_proposal',
            ]),
            proposalSent: resolveStatusValue(['proposal_sent']),
            proposalApproved: resolveStatusValue(['proposal_approved', 'proposal_accepted', 'proposal_confirmed']),
            completed: resolveStatusValue(['completed']),
            canceled: resolveStatusValue(['canceled', 'cancelled']),
        };

        const labelFor = (value: string | null, fallback: string): string => {
            if (!value) {
                return fallback;
            }
            return statusByValue.get(value)?.label ?? fallback;
        };

        return { values, labelFor };
    }, [translatedStatuses]);

    const hasEverBeenProposalSent = useMemo(() => {
        const proposalSentValue = workflow.values.proposalSent;
        if (!proposalSentValue) {
            return false;
        }

        return (workOrder.activities ?? []).some((activity) => activity?.new_status === proposalSentValue);
    }, [workOrder.activities, workflow.values.proposalSent]);

    // Check if work order is in terminal status (completed or canceled)
    const isTerminalStatus = useMemo(() => {
        const terminal = [workflow.values.completed, workflow.values.canceled].filter(Boolean) as string[];
        return terminal.includes(workOrder.status) || statuses.find((s) => s.value === workOrder.status)?.isTerminal === true;
    }, [workOrder.status, statuses, workflow.values.canceled, workflow.values.completed]);

    // Role-based checks for terminal status restriction (Service Supervisor cannot edit terminal work orders)
    const userRole = currentUser?.role;
    const isServiceWorker = userRole === 'Service Worker';
    const isServiceSupervisor = userRole === 'Service Supervisor';
    const isServiceManager = userRole === 'Service Manager';
    const isAdministrator = userRole === 'Administrator';
    const isProposalApprover = isAdministrator || isServiceManager || isServiceSupervisor;

    // Helper: Check if user has manager-like permissions (for workflow actions)
    const isServiceManagerLike = canUpdateStatus || canUpdateDetails || canAssignWorkersPermission;

    // When work order is in terminal status, only Admin and Service Manager can edit
    // Service Supervisor cannot edit terminal work orders
    // Comments and photo uploads are always allowed regardless of status
    const canEditWhenTerminal = isServiceManager || isAdministrator;
    const canEditInCurrentState = isTerminalStatus ? canEditWhenTerminal : true;

    // Permission checks - combine permissions with terminal status restrictions
    const canDelete = canDeleteWorkOrder && (isServiceManager || isAdministrator);
    const canEditCompanies = canUpdateDetails && canEditInCurrentState && (isServiceManager || isAdministrator);
    const canEditDescription = canUpdateDetails && canEditInCurrentState;
    const canEditPriority = canUpdatePriority && canEditInCurrentState;
    const canEditType = canUpdateType && canEditInCurrentState;
    const canEditContact = canUpdateDetails && canEditInCurrentState;
    const canEditAddress = canUpdateDetails && canEditInCurrentState;
    const canAssignWorkers = canAssignWorkersPermission && canEditInCurrentState;

    // Status editing permissions - simplified permission-based logic
    // Users with 'work-orders.update-status' can change to any status except completed/canceled
    // Users need 'work-orders.complete-work-order' to change to completed
    // Users need 'work-orders.can-cancel-work-order' to change to canceled
    const canChangeToStatus = (status: string): boolean => {
        const isProposalSentStatus = normalizeSettingValue(status) === 'proposal_sent';
        const isProposalApprovedStatus = normalizeSettingValue(status) === 'proposal_approved';

        if ((isProposalSentStatus || isProposalApprovedStatus) && !isProposalApprover) {
            return false;
        }

        const isCompleted = status === 'completed' || status === workflow.values.completed;
        const isCanceled = status === 'cancelled' || status === 'canceled' || status === workflow.values.canceled;

        if (isCompleted) {
            return canCompleteWorkOrder && canEditInCurrentState;
        }

        if (isCanceled) {
            return canCancelWorkOrder && canEditInCurrentState;
        }

        // For all other statuses, just need update-status permission
        return canUpdateStatus && canEditInCurrentState;
    };

    // Check if user can see status dropdown (has any status change permission)
    const canSeeStatusOptions = canUpdateStatus || canCompleteWorkOrder || canCancelWorkOrder;
    const isProposalHoldStatus = useMemo(() => {
        return ['proposal_needed', 'proposal_sent', 'proposal_approved'].includes(normalizeSettingValue(workOrder.status));
    }, [workOrder.status]);

    const displayStatus = useMemo(() => {
        if (isServiceWorker && isProposalHoldStatus) {
            return 'on_hold';
        }

        return workOrder.status;
    }, [isServiceWorker, isProposalHoldStatus, workOrder.status]);

    const translatedPriorities = useMemo(
        () =>
            priorities.map((priority) => ({
                ...priority,
                label: t(`settings.work_order_priorities.${normalizeSettingValue(priority.value)}`, priority.label),
            })),
        [priorities, t],
    );

    const translatedTypes = useMemo(
        () =>
            types.map((type) => ({
                ...type,
                label: t(`settings.work_order_types.${normalizeSettingValue(type.value)}`, type.label),
            })),
        [types, t],
    );

    const workflowStages = useMemo(() => {
        const addStage = (
            list: Array<{ id: string; value: string; label: string; orderIndex: number }>,
            value: string | null,
            fallbackLabel: string,
        ) => {
            if (!value) {
                return;
            }
            list.push({
                id: `${value}-${list.length}`,
                value,
                label: workflow.labelFor(value, fallbackLabel),
                orderIndex: list.length,
            });
        };

        const hasProposalFlow = Boolean(workflow.values.proposalNeeded && workflow.values.proposalSent);

        if (!hasProposalFlow) {
            const simple: Array<{ id: string; value: string; label: string; orderIndex: number }> = [];
            addStage(simple, workflow.values.registered, 'Registered');
            addStage(simple, workflow.values.inProgress, 'In Progress');
            addStage(simple, workflow.values.completed, 'Completed');
            return simple;
        }

        const full: Array<{ id: string; value: string; label: string; orderIndex: number }> = [];
        addStage(full, workflow.values.registered, 'Registered');
        addStage(full, workflow.values.inProgress, 'In Progress');
        addStage(full, workflow.values.proposalNeeded, 'Proposal Needed');
        addStage(full, workflow.values.proposalSent, 'Proposal Sent');
        addStage(full, workflow.values.proposalApproved, 'Proposal Approved');
        // Loop back after acceptance
        addStage(full, workflow.values.inProgress, 'In Progress');
        addStage(full, workflow.values.completed, 'Completed');

        return full;
    }, [workflow]);

    const activeWorkflowStageIndex = useMemo(() => {
        if (workflowStages.length === 0) {
            return -1;
        }

        // in_progress appears twice in the canonical workflow; activities help us pick the correct node.
        if (workflow.values.inProgress && workOrder.status === workflow.values.inProgress) {
            const indices = workflowStages.map((s, idx) => (s.value === workflow.values.inProgress ? idx : -1)).filter((idx) => idx !== -1);

            if (indices.length === 0) {
                return -1;
            }

            return hasEverBeenProposalSent ? indices[indices.length - 1] : indices[0];
        }

        return workflowStages.findIndex((s) => s.value === workOrder.status);
    }, [workflowStages, workflow.values.inProgress, workOrder.status, hasEverBeenProposalSent]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const stagesToRender = useMemo(() => {
        if (!isMobile) {
            return { stages: workflowStages, fadeLast: false };
        }

        if (workflowStages.length === 0 || activeWorkflowStageIndex < 0) {
            return { stages: workflowStages, fadeLast: false };
        }

        const start = activeWorkflowStageIndex;
        const end = Math.min(workflowStages.length, start + 3); // current + next 2
        const stages = workflowStages.slice(start, end);
        const fadeLast = end < workflowStages.length;
        return { stages, fadeLast };
    }, [isMobile, workflowStages, activeWorkflowStageIndex]);

    const handleStatusChange = (status: string) => {
        // Check permissions before allowing status change
        if (!canChangeToStatus(status)) {
            toast.error(t('work_orders.show.messages.insufficient_permissions', 'You do not have permission to change to this status'));
            return;
        }

        // Find the status configuration from the statuses array by value
        const statusConfig = statuses.find((s) => s.value === status);
        const isCompleted = status === 'completed' || statusConfig?.value === 'completed' || status === workflow.values.completed;
        const isCanceled =
            status === 'cancelled' ||
            status === 'canceled' ||
            statusConfig?.value === 'cancelled' ||
            statusConfig?.value === 'canceled' ||
            status === workflow.values.canceled;

        // Completed status requires completion dialog
        if (isCompleted) {
            setIsCompleteDialogOpen(true);
            return;
        }

        // Canceled status requires cancellation dialog
        if (isCanceled) {
            setIsCancelDialogOpen(true);
            return;
        }

        // For all other statuses, change immediately via API
        router.patch(
            `/work-orders/${workOrder.id}/status-editable`,
            {
                status,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setWorkOrder((prev) => ({ ...prev, status }));
                    toast.success(t('work_orders.show.messages.status_updated', 'Status updated'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_update_status', 'Failed to update status'));
                },
            },
        );
    };

    const handlePriorityChange = (priority: string) => {
        router.patch(
            `/work-orders/${workOrder.id}/priority`,
            {
                priority,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setWorkOrder((prev) => ({ ...prev, priority }));
                    toast.success(t('work_orders.show.messages.priority_updated', 'Priority updated'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_update_priority', 'Failed to update priority'));
                },
            },
        );
    };

    const handleTypeChange = (type: string) => {
        router.patch(
            `/work-orders/${workOrder.id}/details`,
            {
                work_type: type,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setWorkOrder((prev) => ({ ...prev, work_type: type }));
                    toast.success(t('work_orders.show.messages.type_updated', 'Type updated'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_update_type', 'Failed to update type'));
                },
            },
        );
    };

    const handleDescriptionChange = (description: string) => {
        router.patch(
            `/work-orders/${workOrder.id}/details`,
            {
                description,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setWorkOrder((prev) => ({ ...prev, description }));
                    toast.success(t('work_orders.show.messages.description_updated', 'Description updated'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_update_description', 'Failed to update description'));
                },
            },
        );
    };

    const handleAddressChange = (
        address: string,
        addressData?: {
            lat?: number;
            lng?: number;
            place_id?: string;
            street?: string;
            house_number?: string;
            city?: string;
            postal_code?: string;
        },
    ) => {
        router.patch(
            `/work-orders/${workOrder.id}`,
            {
                service_address_street: addressData?.street || address.split(',')[0] || address,
                service_address_house_number: addressData?.house_number,
                service_address_city: addressData?.city,
                service_address_postal_code: addressData?.postal_code,
                service_address_lat: addressData?.lat,
                service_address_lng: addressData?.lng,
                service_address_place_id: addressData?.place_id,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    // Update state immediately with new address data
                    setWorkOrder((prev) => ({
                        ...prev,
                        service_address: address,
                        service_address_street: addressData?.street || address.split(',')[0] || address,
                        service_address_house_number: addressData?.house_number ?? prev.service_address_house_number,
                        service_address_city: addressData?.city ?? prev.service_address_city,
                        service_address_postal_code: addressData?.postal_code ?? prev.service_address_postal_code,
                        service_address_lat: addressData?.lat ?? prev.service_address_lat,
                        service_address_lng: addressData?.lng ?? prev.service_address_lng,
                        service_address_place_id: addressData?.place_id ?? prev.service_address_place_id,
                    }));
                    // Reload work order data to get updated address from server
                    router.reload({ only: ['workOrder'] });
                    toast.success(t('work_orders.show.messages.address_updated', 'Address updated'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_update_address', 'Failed to update address'));
                },
            },
        );
    };

    const handleComplete = (data: { hours: number; minutes: number; notes?: string }) => {
        router.post(
            `/work-orders/${workOrder.id}/complete`,
            {
                hours: data.hours,
                minutes: data.minutes,
                notes: data.notes,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setIsCompleteDialogOpen(false);
                    // Reload page to get updated work order data
                    router.reload({ only: ['workOrder'] });
                    toast.success(t('work_orders.show.messages.work_order_completed', 'Work order completed'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_complete', 'Failed to complete work order'));
                },
            },
        );
    };

    const handleContactSave = (data: {
        contact_first_name: string;
        contact_last_name: string;
        phone?: string;
        email?: string;
        service_address: string;
        service_address_lat?: number;
        service_address_lng?: number;
        service_address_street?: string;
        service_address_house_number?: string;
        service_address_city?: string;
        service_address_postal_code?: string;
        service_address_place_id?: string;
    }) => {
        router.patch(
            `/work-orders/${workOrder.id}/details`,
            {
                contact_first_name: data.contact_first_name,
                contact_last_name: data.contact_last_name,
                phone: data.phone,
                email: data.email,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setIsContactEditDialogOpen(false);
                    // Reload page to get updated work order data
                    router.reload({ only: ['workOrder'] });
                    toast.success(t('work_orders.show.messages.contact_details_updated', 'Contact details updated'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_update_contact', 'Failed to update contact details'));
                },
            },
        );
    };

    const handleWorkOrderSave = (data: Partial<WorkOrder>) => {
        const payload: {
            company_id?: string;
            paying_company_id?: string | null;
            assigned_user_id?: string | null;
            description?: string;
            status?: string;
            priority?: string;
            estimated_date?: string | null;
            service_address_street?: string;
            service_address_house_number?: string;
            service_address_city?: string;
            service_address_postal_code?: string;
            service_address_lat?: number;
            service_address_lng?: number;
            service_address_place_id?: string;
        } = {};
        if (data.company_id !== undefined) payload.company_id = typeof data.company_id === 'string' ? data.company_id : undefined;
        if (data.paying_company_id !== undefined) {
            payload.paying_company_id = typeof data.paying_company_id === 'string' ? data.paying_company_id : null;
        }
        if (data.assigned_user_id !== undefined) {
            payload.assigned_user_id = typeof data.assigned_user_id === 'string' ? data.assigned_user_id : null;
        }
        if (data.description !== undefined) payload.description = data.description;
        if (data.status !== undefined) payload.status = data.status;
        if (data.priority !== undefined) payload.priority = data.priority;
        if (data.estimated_date !== undefined) payload.estimated_date = data.estimated_date || null;
        if (data.service_address_street !== undefined) payload.service_address_street = data.service_address_street || undefined;
        if (data.service_address_house_number !== undefined) payload.service_address_house_number = data.service_address_house_number || undefined;
        if (data.service_address_city !== undefined) payload.service_address_city = data.service_address_city || undefined;
        if (data.service_address_postal_code !== undefined) payload.service_address_postal_code = data.service_address_postal_code || undefined;
        if (data.service_address_lat !== undefined) payload.service_address_lat = data.service_address_lat ?? undefined;
        if (data.service_address_lng !== undefined) payload.service_address_lng = data.service_address_lng ?? undefined;
        if (data.service_address_place_id !== undefined) payload.service_address_place_id = data.service_address_place_id || undefined;

        router.patch(`/work-orders/${workOrder.id}`, payload, {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                setIsWorkOrderEditDialogOpen(false);
                // Reload page to get updated work order data
                router.reload({ only: ['workOrder'] });
                toast.success(t('work_orders.show.messages.work_order_updated', 'Work order updated'));
            },
            onError: () => {
                toast.error(t('work_orders.show.messages.failed_update_work_order', 'Failed to update work order'));
            },
        });
    };

    const handleAssignWorkers = (assignments: Array<{ userId: string; role: 'lead' | 'helper' }>) => {
        // Filter out any assignments without userId before sending and validate
        const validAssignments = assignments
            .filter((a) => {
                const isValid = a && typeof a === 'object' && a.userId && typeof a.userId === 'string' && a.userId.trim() !== '' && a.role;
                if (!isValid) {
                    console.warn('Invalid assignment filtered out:', a);
                }
                return isValid;
            })
            .map((a) => ({
                user_id: String(a.userId).trim(),
                role: String(a.role) as 'lead' | 'helper',
            }));

        // Ensure we have at least one lead if there are any assignments
        if (validAssignments.length > 0) {
            const leadCount = validAssignments.filter((a) => a.role === 'lead').length;
            if (leadCount === 0) {
                validAssignments[0].role = 'lead';
            }
        }

        router.post(
            `/work-orders/${workOrder.id}/assign-multiple`,
            {
                assignments: validAssignments,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    // Reload page to get updated assignments
                    router.reload({ only: ['workOrder'] });
                    toast.success(t('work_orders.show.messages.workers_assigned', 'Workers assigned'));
                },
                onError: (errors) => {
                    console.error('Assignment error:', errors);
                    console.error('Assignments that failed:', validAssignments);
                    const errorMessage =
                        typeof errors === 'object' && errors !== null && 'message' in errors
                            ? String(errors.message)
                            : t('work_orders.show.messages.failed_assign_workers', 'Failed to assign workers');
                    toast.error(errorMessage);
                },
            },
        );
    };

    const handleSetEstimatedDate = (date: string) => {
        router.patch(
            `/work-orders/${workOrder.id}`,
            {
                estimated_date: date || null,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setWorkOrder((prev) => ({ ...prev, estimated_date: date }));
                    toast.success(t('work_orders.show.messages.estimated_date_updated', 'Estimated date updated'));
                },
                onError: () => {
                    toast.error(t('work_orders.show.messages.failed_update_estimated_date', 'Failed to update estimated date'));
                },
            },
        );
    };

    const handleCompaniesChange = (data: { company_id?: string; paying_company_id?: string | null }) => {
        const payload: {
            company_id?: string;
            paying_company_id?: string | null;
        } = {};

        // Always include company_id if provided and it's a valid non-empty string
        // Note: company_id is required in the database, so we should always send a valid value
        if (data.company_id !== undefined) {
            const trimmedCompanyId = data.company_id?.trim();
            if (trimmedCompanyId && trimmedCompanyId !== '') {
                payload.company_id = trimmedCompanyId;
            }
        }

        // Include paying_company_id (can be null to clear it)
        if (data.paying_company_id !== undefined) {
            const trimmedPayingCompanyId = data.paying_company_id?.trim();
            payload.paying_company_id = trimmedPayingCompanyId && trimmedPayingCompanyId !== '' ? trimmedPayingCompanyId : null;
        }

        // Don't send request if payload is empty
        if (Object.keys(payload).length === 0) {
            return;
        }

        router.patch(`/work-orders/${workOrder.id}`, payload, {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                // Reload page to get updated work order data
                router.reload({ only: ['workOrder'] });
                toast.success(t('work_orders.show.messages.companies_updated', 'Companies updated'));
            },
            onError: () => {
                toast.error(t('work_orders.show.messages.failed_update_companies', 'Failed to update companies'));
            },
        });
    };

    const handlePhotoDelete = async (index: number) => {
        // Use fetch to avoid full-page JSON response on delete
        let csrfToken: string | null = null;
        try {
            csrfToken = getCsrfToken();
        } catch {
            csrfToken = null;
        }

        if (!csrfToken) {
            toast.error(t('toasts.work_orders_show.csrf_token_missing', 'CSRF token not found. Please refresh the page and try again.'));
            return;
        }

        try {
            const response = await fetch(`/work-orders/${workOrder.id}/photos/${index}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/json',
                },
            });
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/41c41c44-42cb-4f68-9600-70f7eae5c694', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    location: 'show.tsx:handlePhotoDelete',
                    message: 'deleteResponse',
                    data: {
                        status: response.status,
                        ok: response.ok,
                        redirected: response.redirected,
                        contentType: response.headers.get('content-type') ?? 'unset',
                    },
                    timestamp: Date.now(),
                    sessionId: 'debug-session',
                    runId: 'pre-fix',
                    hypothesisId: 'D2',
                }),
            }).catch(() => {});
            // #endregion agent log

            let result: { success?: boolean; message?: string; errors?: Record<string, string[]> } | null = null;
            const contentType = response.headers.get('content-type');

            if (response.status !== 204) {
                if (contentType && contentType.includes('application/json')) {
                    result = await response.json();
                } else {
                    const text = await response.text();
                    // #region agent log
                    fetch('http://127.0.0.1:7242/ingest/41c41c44-42cb-4f68-9600-70f7eae5c694', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            location: 'show.tsx:handlePhotoDelete',
                            message: 'deleteNonJson',
                            data: { textLength: text.length, looksLikeHtml: text.trim().startsWith('<') },
                            timestamp: Date.now(),
                            sessionId: 'debug-session',
                            runId: 'pre-fix',
                            hypothesisId: 'D2',
                        }),
                    }).catch(() => {});
                    // #endregion agent log
                    try {
                        result = JSON.parse(text);
                    } catch {
                        result = null;
                    }
                }
            }

            if (response.ok && (response.status === 204 || result?.success || result?.message)) {
                setWorkOrder((prev) => ({
                    ...prev,
                    photos: prev.photos?.filter((_, i) => i !== index) || [],
                }));
                toast.success(result?.message || t('work_orders.show.messages.photo_deleted', 'Photo deleted'));
                return;
            }

            if (result?.errors) {
                const errorMessages = Object.values(result.errors)
                    .flat()
                    .filter((msg): msg is string => typeof msg === 'string');
                toast.error(errorMessages[0] || t('work_orders.show.messages.failed_delete_photo', 'Failed to delete photo'));
                return;
            }

            throw new Error(result?.message || t('work_orders.show.messages.failed_delete_photo', 'Failed to delete photo'));
        } catch (error) {
            console.error('Delete photo error:', error);
            toast.error(error instanceof Error ? error.message : t('work_orders.show.messages.failed_delete_photo', 'Failed to delete photo'));
        }
    };

    const handlePhotoUpload = async (photos: File[]) => {
        if (photos.length === 0) {
            toast.error(t('work_orders.show.photos.upload', 'Please select at least one photo'));
            return;
        }

        const formData = new FormData();
        photos.forEach((photo) => {
            formData.append('photos[]', photo);
        });

        try {
            // Use csrfFetch for file upload since Inertia doesn't handle FormData with JSON responses well
            const response = await csrfFetch(`/work-orders/${workOrder.id}/upload-photos`, {
                method: 'POST',
                body: formData,
            });

            // Handle different response types
            let result;
            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                result = await response.json();
            } else {
                const text = await response.text();
                try {
                    result = JSON.parse(text);
                } catch {
                    throw new Error('Invalid response from server');
                }
            }

            if (response.ok && result.success) {
                setIsPhotoUploadOpen(false);
                // Reload page to get updated photos
                router.reload({ only: ['workOrder'] });
                toast.success(result.message || t('work_orders.show.messages.photos_uploaded', 'Photos uploaded successfully!'));
            } else {
                // Handle validation errors
                if (result.errors) {
                    const errorMessages = Object.values(result.errors)
                        .flat()
                        .filter((msg): msg is string => typeof msg === 'string');
                    toast.error(errorMessages[0] || t('work_orders.show.messages.failed_upload_photos', 'Failed to upload photos'));
                } else {
                    throw new Error(result.message || t('work_orders.show.messages.failed_upload_photos', 'Failed to upload photos'));
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error instanceof Error ? error.message : t('work_orders.show.messages.failed_upload_photos', 'Failed to upload photos'));
        }
    };

    const handleEdit = () => {
        setIsWorkOrderEditDialogOpen(true);
    };

    const handleDelete = () => {
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        router.delete(`/work-orders/${workOrder.id}`, {
            onSuccess: () => {
                router.visit('/work-orders');
                toast.success(t('work_orders.show.messages.work_order_deleted', 'Work order deleted'));
            },
            onError: () => {
                toast.error(t('work_orders.show.messages.failed_delete_work_order', 'Failed to delete work order'));
            },
        });
    };

    const handleCancel = () => {
        setIsCancelDialogOpen(true);
    };

    const handleCancelConfirm = (reason: string) => {
        // Find the correct cancelled status value from statuses array
        const cancelledStatus = statuses.find((s) => s.value === 'cancelled' || s.value === 'canceled')?.value || 'cancelled';

        router.patch(
            `/work-orders/${workOrder.id}/status-editable`,
            {
                status: cancelledStatus,
                cancellation_reason: reason,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setIsCancelDialogOpen(false);
                    // Reload page to get updated work order data
                    router.reload({ only: ['workOrder'] });
                    toast.success(t('work_orders.show.messages.work_order_cancelled', 'Work order cancelled'));
                },
                onError: (errors) => {
                    console.error('Cancel work order error:', errors);
                    let errorMessage = t('work_orders.show.messages.failed_cancel_work_order', 'Failed to cancel work order');

                    if (typeof errors === 'object' && errors !== null) {
                        if ('message' in errors) {
                            errorMessage = String(errors.message);
                        } else if ('error' in errors) {
                            errorMessage = String(errors.error);
                        } else if (Array.isArray(errors)) {
                            errorMessage = errors.join(', ');
                        } else {
                            // Try to extract error from validation errors
                            const errorValues = Object.values(errors).flat();
                            if (errorValues.length > 0) {
                                errorMessage = errorValues.join(', ');
                            }
                        }
                    }

                    toast.error(errorMessage);
                },
            },
        );
    };

    // Get completion info from timetables - only show the most recent completion
    const completedTimetables = useMemo(() => {
        if (!workOrder.timetables || workOrder.timetables.length === 0) {
            return [];
        }

        const allCompletedTimetables = workOrder.timetables.filter((t) => t.completed_at) || [];

        if (allCompletedTimetables.length === 0) {
            return [];
        }

        // Use workOrder.completed_at as the primary reference for the most recent completion
        // This is more reliable than activities since it's updated when the work order is completed
        const workOrderCompletedAt = workOrder.completed_at ? new Date(workOrder.completed_at).getTime() : null;

        const completedStatusValue = workflow.values.completed;
        const activities = workOrder.activities || [];

        // Find all completion activities (status changed to completed)
        const completionActivities = activities
            .filter((activity) => activity.new_status === completedStatusValue)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        // Determine the most recent completion timestamp
        let mostRecentCompletionTimestamp: number | null = null;

        if (workOrderCompletedAt) {
            // Use workOrder.completed_at as the primary reference
            mostRecentCompletionTimestamp = workOrderCompletedAt;
        } else if (completionActivities.length > 0) {
            // Fallback to the most recent completion activity
            mostRecentCompletionTimestamp = new Date(completionActivities[0].created_at).getTime();
        } else {
            // No completion timestamp found - return all completed timetables (legacy case)
            return allCompletedTimetables;
        }

        // Find the next status change after the most recent completion (if any)
        // This helps us exclude timetables from previous completion cycles
        const nextStatusChange = activities
            .filter((activity) => {
                const activityTimestamp = new Date(activity.created_at).getTime();
                // Look for any status change after the completion that moved away from completed
                return activityTimestamp > mostRecentCompletionTimestamp! && activity.old_status === completedStatusValue;
            })
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0];

        const nextStatusChangeTimestamp = nextStatusChange ? new Date(nextStatusChange.created_at).getTime() : null;

        // Filter timetables to only include those from the most recent completion cycle
        // Strategy: Find the completion cycle boundaries using activities, then filter timetables
        // to only those completed within that cycle

        // Find when the status changed away from completed (before the most recent completion)
        // This identifies the boundary between completion cycles
        // Example: completed -> in_progress -> completed
        // We want to find the "in_progress" activity (old_status="completed", new_status="in_progress")
        const statusChangeAwayFromCompleted = activities
            .filter((activity) => {
                const activityTimestamp = new Date(activity.created_at).getTime();
                // Look for activities where status changed FROM completed TO something else
                // and this happened before the most recent completion
                return (
                    activityTimestamp < mostRecentCompletionTimestamp! &&
                    activity.old_status === completedStatusValue &&
                    activity.new_status !== completedStatusValue
                );
            })
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

        // Also check if there was a previous completion activity (in case there are multiple completions)
        // If there are multiple completions, use the previous completion as a boundary
        const previousCompletionActivity =
            completionActivities.length > 1
                ? completionActivities[1] // Second most recent completion
                : null;

        // Determine the start of the current completion cycle
        let cycleStartTimestamp: number;
        if (statusChangeAwayFromCompleted) {
            // The work order was previously completed, then changed status, then completed again
            // Start from when it changed away from completed
            cycleStartTimestamp = new Date(statusChangeAwayFromCompleted.created_at).getTime();
        } else if (previousCompletionActivity) {
            // There was a previous completion, but no status change away from completed found
            // Use the previous completion as the boundary (with a small buffer)
            const previousCompletionTime = new Date(previousCompletionActivity.created_at).getTime();
            // Add a small buffer (1 hour) to account for any timetables completed right at the boundary
            cycleStartTimestamp = previousCompletionTime + 60 * 60 * 1000;
        } else {
            // No previous completion cycle found, this is likely the first completion
            // Use a 24-hour window before the completion timestamp
            const timeWindow = 24 * 60 * 60 * 1000; // 24 hours
            cycleStartTimestamp = mostRecentCompletionTimestamp! - timeWindow;
        }

        // Filter timetables to only include those completed during the current completion cycle
        const relevantTimetables = allCompletedTimetables.filter((timetable) => {
            if (!timetable.completed_at) return false;

            const timetableTimestamp = new Date(timetable.completed_at).getTime();

            // Exclude timetables completed before the start of this completion cycle
            if (timetableTimestamp < cycleStartTimestamp) {
                return false;
            }

            // Exclude timetables completed after the completion timestamp (shouldn't happen, but safety check)
            const bufferAfter = 60 * 60 * 1000; // 1 hour buffer
            if (timetableTimestamp > mostRecentCompletionTimestamp! + bufferAfter) {
                return false;
            }

            // If there's a next status change, exclude timetables completed after it
            if (nextStatusChangeTimestamp !== null && timetableTimestamp >= nextStatusChangeTimestamp) {
                return false;
            }

            return true;
        });

        return relevantTimetables;
    }, [workOrder.timetables, workOrder.activities, workOrder.completed_at, workflow.values.completed]);

    // --- Permission-based workflow actions (mobile sticky bar) ---
    const registeredStatus = workflow.values.registered;
    const inProgressStatus = workflow.values.inProgress;
    const proposalNeededStatus = workflow.values.proposalNeeded;
    const proposalSentStatus = workflow.values.proposalSent;
    const proposalApprovedStatus = workflow.values.proposalApproved;

    // Start work: need update-status permission and be in registered status
    const canStartWork =
        canUpdateStatus &&
        Boolean(registeredStatus && inProgressStatus) &&
        workOrder.status === registeredStatus &&
        canChangeToStatus(inProgressStatus!);

    // Complete work: need complete-work-order permission and be in progress
    const canCompleteWork =
        canCompleteWorkOrder && Boolean(inProgressStatus) && workOrder.status === inProgressStatus && canChangeToStatus(workflow.values.completed!);

    // Request proposal: need update-status permission and be in progress
    const canRequestProposal =
        canUpdateStatus &&
        Boolean(inProgressStatus && proposalNeededStatus) &&
        workOrder.status === inProgressStatus &&
        canChangeToStatus(proposalNeededStatus!);

    // Send proposal: need update-status permission and be in proposal needed status
    const canSendProposal =
        canUpdateStatus &&
        Boolean(proposalNeededStatus && proposalSentStatus) &&
        workOrder.status === proposalNeededStatus &&
        canChangeToStatus(proposalSentStatus!);

    // Mark proposal accepted: need update-status permission and be in proposal sent status
    const canMarkProposalAccepted =
        canUpdateStatus &&
        Boolean(proposalSentStatus && inProgressStatus) &&
        workOrder.status === proposalSentStatus &&
        canChangeToStatus(inProgressStatus!);

    // Continue work after proposal approved
    const canContinueWork =
        canUpdateStatus &&
        Boolean(proposalApprovedStatus && inProgressStatus) &&
        workOrder.status === proposalApprovedStatus &&
        canChangeToStatus(inProgressStatus!);

    const requestProposalLabel = useMemo(() => {
        if (!proposalNeededStatus) {
            return 'Request Proposal';
        }

        // Prefer whatever label is configured in settings (e.g. "Parts Needed")
        return workflow.labelFor(proposalNeededStatus, 'Request Proposal');
    }, [proposalNeededStatus, workflow]);

    const handleStartWork = () => {
        if (!inProgressStatus) {
            toast.error(t('work_orders.show.messages.no_in_progress_status', 'No "in progress" status is configured in Settings.'));
            return;
        }
        handleStatusChange(inProgressStatus);
    };

    const handleRequestProposal = () => {
        if (!proposalNeededStatus) {
            toast.error(t('work_orders.show.messages.no_proposal_needed_status', 'No "proposal/parts needed" status is configured in Settings.'));
            return;
        }
        handleStatusChange(proposalNeededStatus);
    };

    const handleSendProposal = () => {
        if (!proposalSentStatus) {
            toast.error(t('work_orders.show.messages.no_proposal_sent_status', 'No "proposal sent" status is configured in Settings.'));
            return;
        }
        handleStatusChange(proposalSentStatus);
    };

    const handleMarkAccepted = () => {
        if (!inProgressStatus) {
            toast.error(t('toasts.work_orders_show.no_in_progress_status', 'No "in progress" status is configured in Settings.'));
            return;
        }
        handleStatusChange(inProgressStatus);
    };

    const breadcrumbs = [
        { title: t('dashboard.title', 'Dashboard'), href: '/' },
        { title: t('work_orders.index.breadcrumbs.work_orders', 'Work Orders'), href: '/work-orders' },
        { title: workOrder.wo_number || t('work_orders.show.title', 'Work Order'), href: '#' },
    ];

    return (
        <div className="test-work-orders-page">
            <ShowLayout
                breadcrumbs={breadcrumbs}
                title={<span>{workOrder.wo_number || `WO-${workOrder.id.slice(0, 8)}`}</span>}
                sidebar={
                    <WorkOrderSidebar
                        workOrder={workOrder}
                        users={assignableWorkers}
                        companies={companies}
                        onEdit={canEditDescription ? handleEdit : undefined}
                        onDelete={canDelete ? handleDelete : undefined}
                        onComplete={() => setIsCompleteDialogOpen(true)}
                        onCancel={handleCancel}
                        onAssign={canAssignWorkers ? handleAssignWorkers : undefined}
                        onCompaniesChange={canEditCompanies ? handleCompaniesChange : undefined}
                        canDelete={canDelete}
                        canAssignWorkers={canAssignWorkers}
                        canEditCompanies={canEditCompanies}
                        canEditDescription={canEditDescription}
                    />
                }
                sidebarOpen={isSidebarOpen}
                onSidebarOpenChange={setIsSidebarOpen}
            >
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className={cn('bg-muted mb-6 flex items-center gap-1 rounded-xl px-1 py-1', isMobile ? 'w-full' : 'w-fit')}>
                        {visibleTabs.map((tab) => (
                            <TabsTrigger key={tab.value} value={tab.value} className={cn('rounded-md text-sm', isMobile ? 'flex-1' : '')}>
                                {tab.label}
                            </TabsTrigger>
                        ))}
                        {hasOverflow && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        className={cn(
                                            'focus-visible:ring-ring inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                                            overflowTabs.find((t) => t.value === activeTab)
                                                ? 'bg-secondary text-secondary-foreground'
                                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                            isMobile ? 'flex-1' : '',
                                        )}
                                    >
                                        {overflowTabs.find((t) => t.value === activeTab)
                                            ? overflowTabs.find((t) => t.value === activeTab)?.label
                                            : t('work_orders.show.tabs.more', '+{count} more').replace('{count}', String(overflowTabs.length))}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[150px]">
                                    {overflowTabs.map((tab) => (
                                        <DropdownMenuItem
                                            key={tab.value}
                                            onClick={() => setActiveTab(tab.value)}
                                            className={cn(activeTab === tab.value && 'bg-accent font-medium')}
                                        >
                                            {tab.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </TabsList>

                    <TabsContent value="details" className="mt-0">
                        <div className="space-y-8">
                            {/* <div className="space-y-6">
                                <div className="rounded-xl border bg-card p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-semibold text-sm">Status</h3>
                                        <StatusBadge status={workOrder.status} statuses={statuses} />
                                    </div>

                                    {workflowStages.length > 0 && (
                                        <div className={cn(
                                            "relative flex items-center gap-2 mb-4",
                                            isMobile && "overflow-hidden"
                                        )}>
                                            {stagesToRender.stages.map((stage, localIndex) => {
                                                const stageIndex = stage.orderIndex;
                                                const isActive = stageIndex === activeWorkflowStageIndex;
                                                const isPast = activeWorkflowStageIndex !== -1 && stageIndex < activeWorkflowStageIndex;

                                                const isLastVisible = localIndex === stagesToRender.stages.length - 1;
                                                const opacity = isMobile && stagesToRender.fadeLast && isLastVisible ? 0.3 : 1;

                                                return (
                                                    <Fragment key={stage.id}>
                                                        <div
                                                            className={cn(
                                                                "flex-1 flex flex-col items-center transition-all duration-300"
                                                            )}
                                                            style={opacity < 1 ? { opacity } : undefined}
                                                        >
                                                            <div className={cn(
                                                                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors",
                                                                isPast || isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                                                            )}>
                                                                {stageIndex + 1}
                                                            </div>
                                                            <span className="text-xs mt-1 text-center">{stage.label}</span>
                                                        </div>
                                                        {localIndex < stagesToRender.stages.length - 1 && (
                                                            <div
                                                                className={cn(
                                                                    "h-0.5 flex-1 transition-colors",
                                                                    isPast ? "bg-primary" : "bg-muted"
                                                                )}
                                                                style={opacity < 1 ? { opacity } : undefined}
                                                            />
                                                        )}
                                                    </Fragment>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {isTerminalStatus && (
                                        <div className="w-full h-11 flex items-center justify-center text-sm text-muted-foreground">
                                            {workOrder.status === 'completed' ? 'Work order completed' : 'Work order canceled'}
                                        </div>
                                    )}

                                    {isServiceManagerLike && (
                                        <div className="flex flex-wrap gap-2">
                                            {canSeeMoreStatusOptions && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="outline" className="flex-1 min-w-[120px] h-10">
                                                            <MoreVertical className="h-4 w-4 mr-2" />
                                                            Status
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="start" className="w-56">
                                                        {(statuses && statuses.length > 0 ? statuses : [
                                                            { value: 'registered', label: 'Registered' },
                                                            { value: 'in_progress', label: 'In Progress' },
                                                            { value: 'completed', label: 'Completed' },
                                                            { value: 'canceled', label: 'Canceled' },
                                                        ]).map((status) => (
                                                            <DropdownMenuItem
                                                                key={status.value}
                                                                onClick={() => handleStatusChange(status.value)}
                                                                className="cursor-pointer"
                                                            >
                                                                <StatusBadge status={status.value} statuses={statuses} />
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}

                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" className="flex-1 min-w-[120px] h-10">
                                                        <PriorityBadge priority={workOrder.priority || undefined} priorities={priorities} />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start">
                                                    {(priorities && priorities.length > 0 ? priorities : [
                                                        { value: 'low', label: 'Low' },
                                                        { value: 'medium', label: 'Medium' },
                                                        { value: 'high', label: 'High' },
                                                    ]).map((priority) => (
                                                        <DropdownMenuItem
                                                            key={priority.value}
                                                            onClick={() => handlePriorityChange(priority.value)}
                                                            className="cursor-pointer"
                                                        >
                                                            <PriorityBadge priority={priority.value} priorities={priorities} />
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>

                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" className="flex-1 min-w-[120px] h-10">
                                                        <TypeBadge type={(workOrder.work_type as string | undefined) || undefined} types={types} />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start">
                                                    {(types && types.length > 0 ? types : [
                                                        { value: 'no work type', label: 'No work type' },
                                                        { value: 'warranty', label: 'Warranty' },
                                                        { value: 'post warranty', label: 'Post warranty' },
                                                        { value: 'contract', label: 'Contract' },
                                                    ]).map((type) => (
                                                        <DropdownMenuItem
                                                            key={type.value}
                                                            onClick={() => handleTypeChange(type.value)}
                                                            className="cursor-pointer"
                                                        >
                                                            <TypeBadge type={type.value} types={types} />
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <Separator /> */}

                            {/* 2. Important Dates */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">{t('work_orders.show.sections.important_dates', 'Important Dates')}</h3>
                                <div className="grid grid-cols-2 items-start gap-6">
                                    <div>
                                        <p className="text-muted-foreground mb-1 text-sm">{t('work_orders.show.sections.created', 'Created')}</p>
                                        <p className="font-medium">{formatDate(workOrder.created_at)}</p>
                                    </div>
                                    <div>
                                        <SetEstimatedDateSection
                                            estimatedDate={workOrder.estimated_date}
                                            onSetDate={handleSetEstimatedDate}
                                            readOnly={!canEditDescription}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Completion Information */}
                            {workOrder.status === 'completed' ? (
                                <>
                                    <Separator />
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className="text-success-subtle-foreground h-5 w-5" />
                                            <h3 className="text-lg font-semibold">
                                                {t('work_orders.show.sections.completion_details', 'Completion Details')}
                                            </h3>
                                        </div>
                                        <div className="space-y-3">
                                            {completedTimetables.length > 0 && (
                                                <div>
                                                    <p className="text-muted-foreground mb-2 text-sm">
                                                        {t('work_orders.show.sections.completed_by', 'Completed by:')}
                                                    </p>
                                                    <div className="flex flex-wrap gap-3">
                                                        {completedTimetables.map((timetable, idx) => {
                                                            const user = timetable.user;
                                                            if (!user) return null;

                                                            return (
                                                                <div key={timetable.id || idx} className="flex items-center gap-2">
                                                                    <Avatar className="h-8 w-8">
                                                                        <AvatarFallback>
                                                                            {(user.first_name?.[0] ?? '') + (user.last_name?.[0] ?? '')}
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-sm font-medium">
                                                                            {user.first_name} {user.last_name}
                                                                        </span>
                                                                        {timetable.formatted_time && (
                                                                            <span className="text-muted-foreground text-xs">
                                                                                {timetable.formatted_time}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            {workOrder.completed_at && (
                                                <div>
                                                    <p className="text-muted-foreground mb-1 text-sm">
                                                        {t('work_orders.show.sections.completed_on', 'Completed On')}
                                                    </p>
                                                    <p className="font-medium">{formatDateTime(workOrder.completed_at)}</p>
                                                </div>
                                            )}
                                            {completedTimetables.some((t) => t.notes) && (
                                                <div>
                                                    <p className="text-muted-foreground mb-1 text-sm">
                                                        {t('work_orders.show.sections.notes', 'Notes')}
                                                    </p>
                                                    <p className="text-sm">{completedTimetables.find((t) => t.notes)?.notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : null}

                            {/* Cancellation Information */}
                            {(() => {
                                // Check if status is cancelled/canceled using workflow values
                                const isCancelledStatus = workflow.values.canceled
                                    ? workOrder.status === workflow.values.canceled
                                    : workOrder.status === 'cancelled' || workOrder.status === 'canceled';

                                return isCancelledStatus && workOrder.cancellation_reason ? (
                                    <>
                                        <Separator />
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2">
                                                <XCircle className="text-destructive h-5 w-5" />
                                                <h3 className="text-lg font-semibold">
                                                    {t('work_orders.show.sections.cancellation_details', 'Cancellation Details')}
                                                </h3>
                                            </div>
                                            <div className="space-y-3">
                                                {workOrder.updated_at && (
                                                    <div>
                                                        <p className="text-muted-foreground mb-1 text-sm">
                                                            {t('work_orders.show.sections.cancelled_on', 'Cancelled On')}
                                                        </p>
                                                        <p className="font-medium">{formatDateTime(workOrder.updated_at)}</p>
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-muted-foreground mb-1 text-sm">
                                                        {t('work_orders.show.sections.reason', 'Reason')}
                                                    </p>
                                                    <p className="text-sm">{String(workOrder.cancellation_reason || '')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : null;
                            })()}

                            <Separator />

                            {/* 3. Status, Priority, Type Section (Legacy - for desktop compatibility) */}
                            <div className="space-y-6">
                                <div>
                                    <div className="mb-6 flex flex-wrap items-end gap-6">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-muted-foreground text-xs">{t('work_orders.show.sections.status', 'Status')}</label>
                                            {canSeeStatusOptions ? (
                                                <InlineStatusCell
                                                    value={workOrder.status}
                                                    onUpdate={handleStatusChange}
                                                    statuses={translatedStatuses}
                                                    readOnly={!canSeeStatusOptions}
                                                />
                                            ) : (
                                                <StatusBadge status={workOrder.status} statuses={translatedStatuses} />
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-muted-foreground text-xs">
                                                {t('work_orders.show.sections.priority', 'Priority')}
                                            </label>
                                            {canEditPriority ? (
                                                <InlinePriorityCell
                                                    value={workOrder.priority}
                                                    priorities={translatedPriorities}
                                                    onUpdate={handlePriorityChange}
                                                />
                                            ) : (
                                                <PriorityBadge priority={workOrder.priority} priorities={translatedPriorities} />
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-muted-foreground text-xs">{t('work_orders.show.sections.type', 'Type')}</label>
                                            {canEditType ? (
                                                <InlineTypeCell
                                                    value={workOrder.work_type as string | null | undefined}
                                                    types={translatedTypes}
                                                    onUpdate={handleTypeChange}
                                                />
                                            ) : (
                                                <TypeBadge type={(workOrder.work_type as string | undefined) || undefined} types={translatedTypes} />
                                            )}
                                        </div>
                                    </div>

                                    {/* Description Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-semibold">{t('work_orders.show.sections.description', 'Description')}</h3>
                                        </div>
                                        <div className="space-y-2">
                                            <EditableField
                                                value={workOrder.description}
                                                onSave={handleDescriptionChange}
                                                multiline
                                                placeholder={t('work_orders.show.service_details.no_description_provided', 'Add description...')}
                                                readOnly={!canEditDescription}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* 3. Service Address */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">{t('work_orders.show.sections.service_address', 'Service Address')}</h3>
                                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                    <div className="space-y-2">
                                        <EditableAddressField
                                            value={displayAddress}
                                            onSave={handleAddressChange}
                                            label={t('work_orders.show.sections.address', 'Address')}
                                            placeholder={t('work_orders.forms.create.placeholders.enter_address', 'Enter service address...')}
                                            readOnly={!canEditAddress}
                                        />
                                        {workOrder.service_address_city && (
                                            <p className="text-muted-foreground mt-2 text-sm">
                                                {workOrder.service_address_city}
                                                {workOrder.service_address_postal_code && `, ${workOrder.service_address_postal_code}`}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <AddressMapCard
                                            address={displayAddress}
                                            lat={workOrder.service_address_lat ?? undefined}
                                            lng={workOrder.service_address_lng ?? undefined}
                                        />
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* 4. Contact Information */}
                            <div className="space-y-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <h3 className="text-lg font-semibold">
                                        {t('work_orders.show.sections.contact_information', 'Contact Information')}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        {/* Call and Email Action Buttons */}
                                        {(workOrder.phone || workOrder.email) && (
                                            <div className="flex items-center gap-2">
                                                {workOrder.phone && (
                                                    <Button variant="outline" size="sm" asChild className="gap-2">
                                                        <a href={`tel:${workOrder.phone}`}>
                                                            <Phone className="h-4 w-4" />
                                                            <span className="hidden sm:inline">{t('work_orders.show.actions.call', 'Call')}</span>
                                                        </a>
                                                    </Button>
                                                )}
                                                {workOrder.email && (
                                                    <Button variant="outline" size="sm" asChild className="gap-2">
                                                        <a href={`mailto:${workOrder.email}`}>
                                                            <Mail className="h-4 w-4" />
                                                            <span className="hidden sm:inline">
                                                                {t('work_orders.show.actions.email_action', 'Email')}
                                                            </span>
                                                        </a>
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                        {canEditContact && (
                                            <Button variant="outline" size="sm" onClick={() => setIsContactEditDialogOpen(true)}>
                                                <Edit className="mr-2 h-4 w-4" />
                                                {t('work_orders.show.actions.edit', 'Edit')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <EditableField
                                        value={`${workOrder.contact_first_name} ${workOrder.contact_last_name}`}
                                        onSave={(value) => {
                                            const parts = value.split(' ');
                                            handleContactSave({
                                                contact_first_name: parts[0] || '',
                                                contact_last_name: parts.slice(1).join(' ') || '',
                                                phone: workOrder.phone || '',
                                                email: workOrder.email || '',
                                                service_address: displayAddress,
                                                service_address_lat: workOrder.service_address_lat ?? undefined,
                                                service_address_lng: workOrder.service_address_lng ?? undefined,
                                                service_address_street: workOrder.service_address_street ?? undefined,
                                                service_address_house_number: workOrder.service_address_house_number ?? undefined,
                                                service_address_city: workOrder.service_address_city ?? undefined,
                                                service_address_postal_code: workOrder.service_address_postal_code ?? undefined,
                                                service_address_place_id: workOrder.service_address_place_id ?? undefined,
                                            });
                                        }}
                                        label={t('work_orders.show.sections.name', 'Name')}
                                        readOnly={!canEditContact}
                                    />
                                    <EditableField
                                        value={workOrder.phone || ''}
                                        onSave={(value) =>
                                            handleContactSave({
                                                contact_first_name: workOrder.contact_first_name,
                                                contact_last_name: workOrder.contact_last_name,
                                                phone: value,
                                                email: workOrder.email || '',
                                                service_address: displayAddress,
                                                service_address_lat: workOrder.service_address_lat ?? undefined,
                                                service_address_lng: workOrder.service_address_lng ?? undefined,
                                                service_address_street: workOrder.service_address_street ?? undefined,
                                                service_address_house_number: workOrder.service_address_house_number ?? undefined,
                                                service_address_city: workOrder.service_address_city ?? undefined,
                                                service_address_postal_code: workOrder.service_address_postal_code ?? undefined,
                                                service_address_place_id: workOrder.service_address_place_id ?? undefined,
                                            })
                                        }
                                        label={t('work_orders.show.sections.phone', 'Phone')}
                                        placeholder={t('work_orders.show.empty.no_phone', 'No phone number')}
                                        readOnly={!canEditContact}
                                    />
                                    <EditableField
                                        value={workOrder.email || ''}
                                        onSave={(value) =>
                                            handleContactSave({
                                                contact_first_name: workOrder.contact_first_name,
                                                contact_last_name: workOrder.contact_last_name,
                                                phone: workOrder.phone || '',
                                                email: value,
                                                service_address: displayAddress,
                                                service_address_lat: workOrder.service_address_lat ?? undefined,
                                                service_address_lng: workOrder.service_address_lng ?? undefined,
                                                service_address_street: workOrder.service_address_street ?? undefined,
                                                service_address_house_number: workOrder.service_address_house_number ?? undefined,
                                                service_address_city: workOrder.service_address_city ?? undefined,
                                                service_address_postal_code: workOrder.service_address_postal_code ?? undefined,
                                                service_address_place_id: workOrder.service_address_place_id ?? undefined,
                                            })
                                        }
                                        label={t('work_orders.show.sections.email', 'Email')}
                                        placeholder={t('work_orders.show.empty.no_email', 'No email address')}
                                        readOnly={!canEditContact}
                                    />
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="photos" className="mt-0">
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <ImageIcon className="text-muted-foreground h-5 w-5" />
                                    <h3 className="text-lg font-semibold">{t('work_orders.show.tabs.photos', 'Photos')}</h3>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => setIsPhotoUploadOpen(true)}>
                                    <Upload className="mr-2 h-4 w-4" />
                                    {t('work_orders.show.actions.upload_photos', 'Upload Photos')}
                                </Button>
                            </div>

                            <PhotoGallery photos={workOrder.photos || []} onDelete={handlePhotoDelete} canDelete={true} />
                        </div>
                    </TabsContent>

                    <TabsContent value="comments" className="mt-0 h-full">
                        <div className="h-full">
                            {/* <WorkOrderComments
                                workOrderId={workOrder.id}
                                currentUser={currentUser}
                                users={users}
                            /> */}

                            <UniversalComments model="WorkOrder" entityId={workOrder.id} users={users} currentUser={currentUser} />
                        </div>
                    </TabsContent>
                </Tabs>

                {/* Complete Work Order Dialog */}
                <CompleteWorkOrderDialog
                    workOrder={workOrder}
                    isOpen={isCompleteDialogOpen}
                    onClose={() => setIsCompleteDialogOpen(false)}
                    onComplete={handleComplete}
                />

                {/* Cancel Work Order Dialog */}
                <CancelWorkOrderDialog
                    workOrder={workOrder}
                    isOpen={isCancelDialogOpen}
                    onClose={() => setIsCancelDialogOpen(false)}
                    onCancel={handleCancelConfirm}
                />

                {/* Delete Confirmation Dialog */}
                <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('work_orders.dialogs.delete.title', 'Delete Work Order')}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t(
                                    'work_orders.dialogs.delete.description',
                                    'Are you sure you want to delete this work order? This action cannot be undone.',
                                )}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="gap-2">
                            <AlertDialogCancel>{t('work_orders.dialogs.delete.cancel', 'Cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDeleteConfirm}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                {t('work_orders.dialogs.delete.delete', 'Delete')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Edit Contact Dialog */}
                <EditContactDialog
                    isOpen={isContactEditDialogOpen}
                    onClose={() => setIsContactEditDialogOpen(false)}
                    initialData={{
                        contact_first_name: workOrder.contact_first_name,
                        contact_last_name: workOrder.contact_last_name,
                        phone: workOrder.phone,
                        email: workOrder.email,
                    }}
                    onSave={(contactData) => {
                        handleContactSave({
                            ...contactData,
                            service_address: displayAddress,
                            service_address_lat: workOrder.service_address_lat ?? undefined,
                            service_address_lng: workOrder.service_address_lng ?? undefined,
                            service_address_street: workOrder.service_address_street ?? undefined,
                            service_address_house_number: workOrder.service_address_house_number ?? undefined,
                            service_address_city: workOrder.service_address_city ?? undefined,
                            service_address_postal_code: workOrder.service_address_postal_code ?? undefined,
                            service_address_place_id: workOrder.service_address_place_id ?? undefined,
                        });
                    }}
                />

                {/* Edit Work Order Dialog */}
                <EditWorkOrderDialog
                    isOpen={isWorkOrderEditDialogOpen}
                    onClose={() => setIsWorkOrderEditDialogOpen(false)}
                    workOrder={workOrder}
                    users={users}
                    companies={companies}
                    onSave={handleWorkOrderSave}
                    canEditCompanies={canEditCompanies}
                    canEditDescription={canEditDescription}
                    statuses={translatedStatuses}
                    priorities={translatedPriorities}
                    types={translatedTypes}
                    canEditStatusPriorityType={canEditDescription}
                />

                {/* Photo Upload Dialog */}
                <PhotoUploadDialog isOpen={isPhotoUploadOpen} onClose={() => setIsPhotoUploadOpen(false)} onUpload={handlePhotoUpload} />
            </ShowLayout>

            {/* Mobile Sticky Bottom Action Bar */}
            {isMobile && isServiceManagerLike && !isTerminalStatus && activeTab !== 'comments' && (
                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 safe-area-inset-bottom fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg backdrop-blur md:hidden">
                    <div className="mx-auto flex max-w-screen-xl gap-2">
                        {/* Left side - Action buttons (3/4 width) */}
                        <div className="flex flex-[3] gap-2">
                            {/* Service Worker actions */}
                            {isServiceWorker && isProposalHoldStatus && (
                                <Button size="lg" className="h-12 flex-1 text-base font-semibold" disabled>
                                    {t('work_orders.show.workflow.on_hold', 'On Hold')}
                                </Button>
                            )}

                            {canStartWork && (
                                <Button size="lg" className="h-12 flex-1 text-base font-semibold" onClick={handleStartWork}>
                                    <Play className="mr-2 h-5 w-5" />
                                    {t('work_orders.show.workflow.start_work', 'Start Work')}
                                </Button>
                            )}

                            {canCompleteWork && (
                                <Button
                                    size="lg"
                                    className="bg-success text-success-foreground hover:bg-success/90 h-12 flex-[1] justify-center"
                                    onClick={() => setIsCompleteDialogOpen(true)}
                                    aria-label={t('work_orders.show.workflow.complete', 'Complete')}
                                >
                                    <CheckCircle2 className="h-5 w-5" />
                                </Button>
                            )}

                            {canRequestProposal && (
                                <Button variant="outline" size="lg" className="h-12 flex-[2] text-base font-semibold" onClick={handleRequestProposal}>
                                    <Mail className="mr-2 h-5 w-5" />
                                    {requestProposalLabel}
                                </Button>
                            )}

                            {/* Service Manager/Supervisor/Admin actions */}
                            {canSendProposal && (
                                <Button size="lg" className="h-12 flex-1 text-base font-semibold" onClick={handleSendProposal}>
                                    <Mail className="mr-2 h-5 w-5" />
                                    {t('work_orders.show.workflow.send_proposal', 'Send Proposal')}
                                </Button>
                            )}

                            {canMarkProposalAccepted && (
                                <Button size="lg" className="h-12 flex-1 text-base font-semibold" onClick={handleMarkAccepted}>
                                    <Play className="mr-2 h-5 w-5" />
                                    {t('work_orders.show.workflow.back_to_work', 'Back to Work')}
                                </Button>
                            )}

                            {canContinueWork && (
                                <Button size="lg" className="h-12 flex-1 text-base font-semibold" onClick={handleMarkAccepted}>
                                    <Play className="mr-2 h-5 w-5" />
                                    {t('work_orders.show.workflow.continue_work', 'Continue Work')}
                                </Button>
                            )}
                        </div>

                        {/* Right side - Sidebar button (1/4 width) */}
                        <Button variant="outline" size="lg" className="h-12 flex-[1] text-base font-semibold" onClick={() => setIsSidebarOpen(true)}>
                            <MoreVertical className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Show terminal status badge if work order is completed/canceled */}
            {isMobile && (isServiceManager || isServiceSupervisor || isAdministrator) && isTerminalStatus && activeTab !== 'comments' && (
                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 safe-area-inset-bottom fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg backdrop-blur md:hidden">
                    <div className="mx-auto flex max-w-screen-xl gap-2">
                        {/* Left side - Status badge (3/4 width) */}
                        <div className="flex flex-[3] items-center justify-center">
                            <StatusBadge status={displayStatus} statuses={translatedStatuses} />
                        </div>
                        {/* Right side - Sidebar button (1/4 width) */}
                        <Button variant="outline" size="lg" className="h-12 flex-[1] text-base font-semibold" onClick={() => setIsSidebarOpen(true)}>
                            <MoreVertical className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Add bottom padding on mobile to account for sticky bar */}
            {isMobile && isServiceManagerLike && activeTab !== 'comments' && <div className="h-20 md:h-0" />}
        </div>
    );
}
